/**
 * Admin Dashboard Tab (Tab 1)
 * 
 * Matrix view of certification status by Process Owner and Quarter
 * Features:
 * - Collapsible sections grouped by Process Owner
 * - Q1-Q4 columns with sign-off dates
 * - Remind Mode with batch email functionality
 * - Each ProcessOwnerSection fetches its own processes (lazy loading)
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Checkbox, Dropdown, message, Modal, Radio, Tooltip, Table } from 'antd';
import type { MenuProps, RadioChangeEvent, TableColumnsType } from 'antd';
import { DownOutlined, RightOutlined, MoreOutlined, MailOutlined, DownloadOutlined, ExportOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ExportPanel from '../../../../../components/common/ExportPanel';
import { RootState, AppDispatch } from '../../../../../store';
import {
  enterRemindMode,
  exitRemindMode,
  toggleProcessForRemind,
  selectAllProcessesForRemind,
  setProcessesForOwner,
  setSelectedProcessOwner,
} from '../../../../../store/slices/certificationSlice';
import {
  sendCertificationReminder,
  formatCertificationDate,
  getProcessCertificationsForOwner,
  downloadCertification,
  type ProcessOwner,
  type ProcessCertification,
  type Quarter,
} from '../../../../../services/certificationService';

// ========================================
// Constants
// ========================================

// Material color palette for sections (same as CYCLE_COLORS in hierarchyService)
const SECTION_COLORS = [
  '#E3F2FD', // Blue 50
  '#F3E5F5', // Purple 50
  '#E8F5E9', // Green 50
  '#FFF3E0', // Orange 50
  '#FCE4EC', // Pink 50
  '#E0F2F1', // Teal 50
  '#FFF9C4', // Yellow 50
  '#F1F8E9', // Light Green 50
  '#E1F5FE', // Light Blue 50
  '#FBE9E7', // Deep Orange 50
];

// ========================================
// Styled Components
// ========================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const OwnerSection = styled.div<{ $bgColor: string }>`
  margin-bottom: 0;
  background: ${props => props.$bgColor};
  padding: 16px 2px 2px 2px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const OwnerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  margin-left: 16px;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const CollapseIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  transition: transform 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const OwnerName = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #2563EB;
  flex: 1;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const OwnerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 16px;
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 6px;
  overflow: hidden;

  .ant-table {
    border-radius: 4px;
    overflow: hidden;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    color: #6B7280;
    padding: 12px 16px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    
    &:last-child {
      border-right: none;
    }
    
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 14px;
    color: #1F2937;
    padding: 12px 16px;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    
    &:last-child {
      border-right: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }

  .ant-table-placeholder .ant-table-cell {
    text-align: center;
    color: #9CA3AF;
    font-style: italic;
    padding: 24px 8px;
  }
`;

const ProcessChip = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const DateCell = styled.span`
  color: #6B7280;
  font-size: 12px;
`;

const RemindCTA = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border-radius: 0 0 6px 6px;
  border-top: 1px solid #E5E7EB;
`;

const RemindAllFooter = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  z-index: 10;
`;

const StyledOutlinedButton = styled(Button)`
  height: 36px;
  padding: 0 24px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #2563EB;
  color: #2563EB;

  &:disabled {
    color: #9CA3AF !important;
    border-color: #D1D5DB !important;
  }

  &:hover:not(:disabled) {
    border-color: #1D4ED8 !important;
    color: #1D4ED8 !important;
  }
`;

const StyledFilledButton = styled(Button)`
  height: 36px;
  padding: 0 24px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  background: #2563EB;
  border-color: #2563EB;
  color: #FFFFFF;
  
  &:hover:not(:disabled) {
    background: #1D4ED8 !important;
    border-color: #1D4ED8 !important;
    color: #FFFFFF !important;
  }
  
  &:disabled {
    background: #9CA3AF !important;
    border-color: #9CA3AF !important;
  }
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const MenuItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: inherit;
`;

const LoadingStateWrapper = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: white;
  border-radius: 6px;
`;

const CircleSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid #E5E7EB;
  border-top-color: #2563EB;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 12px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  color: #6B7280;
  font-size: 13px;
  font-weight: 500;
`;

// Download Modal Styled Components
const DownloadModalContent = styled.div`
  text-align: center;
  padding: 8px 0;
`;

const DownloadModalTitle = styled.h3`
  font-family: Inter;
  font-weight: 700;
  font-size: 20px;
  color: #1F2937;
  margin: 0 0 8px 0;
`;

const DownloadModalSubtitle = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
  margin-bottom: 24px;
`;

const QuarterSelectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

const QuarterOption = styled.div<{ $isAvailable: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid ${props => props.$isAvailable ? '#E5E7EB' : '#F3F4F6'};
  border-radius: 8px;
  background: ${props => props.$isAvailable ? '#FFFFFF' : '#F9FAFB'};
  cursor: ${props => props.$isAvailable ? 'pointer' : 'not-allowed'};
  transition: all 0.2s;
  
  &:hover {
    ${props => props.$isAvailable && `
      border-color: #2563EB;
      background: #EFF6FF;
    `}
  }

  .ant-radio-wrapper {
    width: 100%;
    margin: 0;
    
    span:last-child {
      flex: 1;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
`;

const QuarterLabel = styled.span<{ $isAvailable: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  margin-right: 4px;
  color: ${props => props.$isAvailable ? '#1F2937' : '#9CA3AF'};
`;

const QuarterDate = styled.span<{ $isAvailable: boolean }>`
  font-family: Inter;
  font-size: 12px;
  color: ${props => props.$isAvailable ? '#6B7280' : '#D1D5DB'};
`;

const DownloadButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
`;

const DownloadCancelButton = styled(Button)`
  min-width: 120px;
  height: 46px;
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #2563EB;
  color: #2563EB;
  
  &:hover {
    border-color: #1D4ED8 !important;
    color: #1D4ED8 !important;
  }
`;

const DownloadConfirmButton = styled(Button)`
  min-width: 120px;
  height: 46px;
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  border-radius: 8px;
  background: #2563EB;
  border-color: #2563EB;
  color: #FFFFFF;
  
  &:hover:not(:disabled) {
    background: #1D4ED8 !important;
    border-color: #1D4ED8 !important;
    color: #FFFFFF !important;
  }
  
  &:disabled {
    background: #9CA3AF !important;
    border-color: #9CA3AF !important;
  }
`;

const StyledDownloadModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    padding: 24px;
  }
`;

// ========================================
// ProcessOwnerSection Component
// ========================================

interface ProcessOwnerSectionProps {
  index: number;
  processOwner: ProcessOwner;
  annualReportId: string;
  isExpanded: boolean;
  isRemindMode: boolean;
  activeRemindOwnerId: string | null;
  selectedProcessIdsForRemind: string[];
  onToggleExpand: (ownerId: string) => void;
  onOwnerNameClick: (ownerId: string) => void;
  onProcessClick: (processId: string) => void;
  onRemindClick: (ownerId: string) => void;
  onCancelRemind: () => void;
  onToggleProcessRemind: (processId: string) => void;
  onSendReminder: () => Promise<void>;
  sendingReminder: boolean;
  onDownloadCertification: (process: ProcessCertification, ownerId: string) => void;
  onExportSection: (owner: ProcessOwner, processes: ProcessCertification[]) => void;
  isRemindAllMode?: boolean;
  remindAllSelectedProcessIds?: string[];
  onRemindAllToggleProcess?: (processId: string) => void;
  onToggleAllOwnerProcesses?: () => void;
}

const ProcessOwnerSection: React.FC<ProcessOwnerSectionProps> = ({
  index,
  processOwner,
  annualReportId,
  isExpanded,
  isRemindMode,
  activeRemindOwnerId,
  selectedProcessIdsForRemind,
  onToggleExpand,
  onOwnerNameClick,
  onProcessClick,
  onRemindClick,
  onCancelRemind,
  onToggleProcessRemind,
  onSendReminder,
  sendingReminder,
  onDownloadCertification,
  onExportSection,
  isRemindAllMode,
  remindAllSelectedProcessIds,
  onRemindAllToggleProcess,
  onToggleAllOwnerProcesses,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Get cached processes from Redux
  const { processesByOwnerId, fetchedOwnerIds } = useSelector(
    (state: RootState) => state.certification
  );
  
  const processes = processesByOwnerId[processOwner.id] || [];
  const hasCachedProcesses = fetchedOwnerIds.includes(processOwner.id);
  
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(false);
  // Stores the annualReportId for which this owner's processes were last fetched
  const lastFetchedForReport = useRef<string | null>(null);

  // Fetch processes when component mounts (Offline-first with smart caching)
  useEffect(() => {
    const fetchProcesses = async () => {
      const alreadyFetchedForThisReport = lastFetchedForReport.current === annualReportId;

      // Skip if we already fetched for this report AND cache is still valid
      if (alreadyFetchedForThisReport && hasCachedProcesses) {
        return;
      }

      // Mark as fetched for this report
      lastFetchedForReport.current = annualReportId;

      setIsLoadingProcesses(true);

      try {
        const fetchedProcesses = await getProcessCertificationsForOwner(
          annualReportId,
          processOwner.id
        );
        
        dispatch(setProcessesForOwner({
          ownerId: processOwner.id,
          processes: fetchedProcesses,
        }));
      } catch (error) {
        console.error(`❌ Failed to fetch processes for ${processOwner.id}:`, error);
        message.error(`Failed to load processes for ${processOwner.processOwnerName}`);
      } finally {
        setIsLoadingProcesses(false);
      }
    };

    fetchProcesses();
  }, [processOwner.id, processOwner.processOwnerName, annualReportId, hasCachedProcesses, dispatch]);
  
  const isInPerOwnerRemindMode = isRemindMode && activeRemindOwnerId === processOwner.id;
  const isInAnyRemindMode = isInPerOwnerRemindMode || !!isRemindAllMode;
  const bgColor = SECTION_COLORS[index % SECTION_COLORS.length];

  const effectiveSelectedIdsForOwner = isRemindAllMode
    ? (remindAllSelectedProcessIds || [])
    : selectedProcessIdsForRemind;
  const allOwnerProcessIds = processes.map(p => p.processId);
  const ownerSelectedCount = allOwnerProcessIds.filter(id => effectiveSelectedIdsForOwner.includes(id)).length;
  const isOwnerAllSelected = allOwnerProcessIds.length > 0 && ownerSelectedCount === allOwnerProcessIds.length;
  const isOwnerIndeterminate = ownerSelectedCount > 0 && ownerSelectedCount < allOwnerProcessIds.length;
  const effectiveExpanded = isInAnyRemindMode ? true : isExpanded;
  
  // Get menu items for owner action
  const getOwnerMenuItems = (): MenuProps['items'] => [
    {
      key: 'remind',
      label: (
        <MenuItemContent>
          <MailOutlined />
          <MenuItemText>Remind</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => onRemindClick(processOwner.id),
    },
    {
      key: 'export',
      label: (
        <MenuItemContent>
          <ExportOutlined />
          <MenuItemText>Export</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => onExportSection(processOwner, processes),
      disabled: processes.length === 0,
    },
  ];
  
  // Get menu items for process action
  const getProcessMenuItems = (process: ProcessCertification): MenuProps['items'] => {
    // Check if any certification date exists for this process
    const hasAnyCertification = !!(
      process.q1SignOffDate ||
      process.q2SignOffDate ||
      process.q3SignOffDate ||
      process.q4SignOffDate
    );

    const downloadMenuItem = (
      <MenuItemContent>
        <DownloadOutlined />
        <MenuItemText>Download Certification</MenuItemText>
      </MenuItemContent>
    );

    return [
      {
        key: 'remind',
        label: (
          <MenuItemContent>
            <MailOutlined />
            <MenuItemText>Remind</MenuItemText>
          </MenuItemContent>
        ),
        onClick: () => onRemindClick(processOwner.id),
      },
      {
        key: 'download',
        label: hasAnyCertification ? downloadMenuItem : (
          <Tooltip title="No certifications completed for this process" placement="top">
            <div>{downloadMenuItem}</div>
          </Tooltip>
        ),
        disabled: !hasAnyCertification,
        onClick: () => onDownloadCertification(process, processOwner.id),
      },
    ];
  };
  
  // Table columns definition
  const columns: TableColumnsType<ProcessCertification> = useMemo(() => {
    const effectiveSelectedIds = isRemindAllMode
      ? (remindAllSelectedProcessIds || [])
      : selectedProcessIdsForRemind;
    const effectiveToggleHandler = isRemindAllMode
      ? (onRemindAllToggleProcess || (() => {}))
      : onToggleProcessRemind;

    const baseColumns: TableColumnsType<ProcessCertification> = [];
    
    // Checkbox column for remind mode (per-owner or remind-all)
    if (isInAnyRemindMode) {
      baseColumns.push({
        title: '',
        key: 'checkbox',
        width: 40,
        align: 'center' as const,
        render: (_: unknown, record: ProcessCertification) => (
          <Checkbox
            checked={effectiveSelectedIds.includes(record.processId)}
            onChange={() => effectiveToggleHandler(record.processId)}
          />
        ),
      });
    }
    
    // Process column
    baseColumns.push({
      title: 'Process',
      dataIndex: 'processLabel',
      key: 'process',
      render: (text: string, record: ProcessCertification) => (
        <ProcessChip onClick={() => onProcessClick(record.processId)}>
          {text}
        </ProcessChip>
      ),
    });
    
    // Quarter columns
    baseColumns.push(
      {
        title: 'Q1',
        dataIndex: 'q1SignOffDate',
        key: 'q1',
        width: 150,
        align: 'center' as const,
        render: (date: string | null) => <DateCell>{formatCertificationDate(date)}</DateCell>,
      },
      {
        title: 'Q2',
        dataIndex: 'q2SignOffDate',
        key: 'q2',
        width: 150,
        align: 'center' as const,
        render: (date: string | null) => <DateCell>{formatCertificationDate(date)}</DateCell>,
      },
      {
        title: 'Q3',
        dataIndex: 'q3SignOffDate',
        key: 'q3',
        width: 150,
        align: 'center' as const,
        render: (date: string | null) => <DateCell>{formatCertificationDate(date)}</DateCell>,
      },
      {
        title: 'Q4',
        dataIndex: 'q4SignOffDate',
        key: 'q4',
        width: 150,
        align: 'center' as const,
        render: (date: string | null) => <DateCell>{formatCertificationDate(date)}</DateCell>,
      }
    );
    
    // Action column (hidden in any remind mode)
    if (!isInAnyRemindMode) {
      baseColumns.push({
        title: 'Action',
        key: 'action',
        width: 80,
        align: 'center' as const,
        render: (_: unknown, record: ProcessCertification) => (
          <Dropdown
            menu={{ items: getProcessMenuItems(record) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <ActionButton onClick={(e) => e.stopPropagation()}>
              <MoreOutlined />
            </ActionButton>
          </Dropdown>
        ),
      });
    }
    
    return baseColumns;
  }, [isInAnyRemindMode, isRemindAllMode, remindAllSelectedProcessIds, selectedProcessIdsForRemind, onRemindAllToggleProcess, onToggleProcessRemind, onProcessClick]);

  return (
    <OwnerSection $bgColor={bgColor}>
      <OwnerHeader onClick={() => {
        if (!isInAnyRemindMode) onToggleExpand(processOwner.id);
      }}>
        {isInAnyRemindMode ? (
          <Checkbox
            checked={isOwnerAllSelected}
            indeterminate={isOwnerIndeterminate}
            onClick={(e) => {
              e.stopPropagation();
              onToggleAllOwnerProcesses?.();
            }}
          />
        ) : (
          <CollapseIcon>
            {isExpanded ? <DownOutlined /> : <RightOutlined />}
          </CollapseIcon>
        )}
        <OwnerName 
          onClick={(e) => {
            e.stopPropagation();
            onOwnerNameClick(processOwner.id);
          }}
        >
          {processOwner.processOwnerName}
        </OwnerName>
        {!isInAnyRemindMode && (
          <OwnerActions onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{ items: getOwnerMenuItems() }}
              trigger={['click']}
              placement="bottomRight"
            >
              <ActionButton>
                <MoreOutlined />
              </ActionButton>
            </Dropdown>
          </OwnerActions>
        )}
      </OwnerHeader>
      
      {effectiveExpanded && (
        <>
          {isLoadingProcesses ? (
            <LoadingStateWrapper>
              <CircleSpinner />
              <LoadingText>Loading processes...</LoadingText>
            </LoadingStateWrapper>
          ) : (
            <>
              <TableWrapper>
                <Table
                  dataSource={processes}
                  columns={columns}
                  rowKey="id"
                  pagination={false}
                  size="middle"
                  locale={{
                    emptyText: 'This owner has not been assigned any processes.',
                  }}
                />
              </TableWrapper>
              
              {isInPerOwnerRemindMode && !isRemindAllMode && (
                <RemindCTA>
                  <StyledOutlinedButton onClick={onCancelRemind}>
                    Cancel
                  </StyledOutlinedButton>
                  <StyledFilledButton
                    type="primary"
                    icon={<MailOutlined />}
                    loading={sendingReminder}
                    disabled={selectedProcessIdsForRemind.length === 0}
                    onClick={onSendReminder}
                  >
                    Send Reminder ({selectedProcessIdsForRemind.length})
                  </StyledFilledButton>
                </RemindCTA>
              )}
            </>
          )}
        </>
      )}
    </OwnerSection>
  );
};

// ========================================
// Main Component
// ========================================

// Download modal state interface
interface DownloadModalState {
  isOpen: boolean;
  process: ProcessCertification | null;
  processOwnerId: string;
  selectedQuarter: Quarter | null;
}

// ========================================
// Component Props
// ========================================

interface AdminDashboardTabProps {
  filterProcessOwnerId?: string | null;
  /** Trigger to open export panel from parent */
  triggerExport?: boolean;
  /** Callback when export panel is closed */
  onExportClose?: () => void;
  /** Whether "Remind All" mode is active (controlled by parent) */
  isRemindAllMode?: boolean;
  /** Callback to exit "Remind All" mode */
  onExitRemindAll?: () => void;
}

const AdminDashboardTab: React.FC<AdminDashboardTabProps> = ({
  filterProcessOwnerId,
  triggerExport,
  onExportClose,
  isRemindAllMode,
  onExitRemindAll,
}) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  
  // Get annual report for API calls
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  
  const {
    processOwners: allProcessOwners,
    isRemindMode,
    selectedProcessIdsForRemind,
    processesByOwnerId,
  } = useSelector((state: RootState) => state.certification);
  
  // Filter process owners if filterProcessOwnerId is provided
  const processOwners = useMemo(() => {
    if (filterProcessOwnerId) {
      return allProcessOwners.filter(owner => owner.id === filterProcessOwnerId);
    }
    return allProcessOwners;
  }, [allProcessOwners, filterProcessOwnerId]);
  
  const [sendingReminder, setSendingReminder] = useState(false);
  const [activeRemindOwnerId, setActiveRemindOwnerId] = useState<string | null>(null);
  // Local state for expanded owners - expand all by default
  const [expandedOwnerIds, setExpandedOwnerIds] = useState<string[]>([]);
  
  // Remind All mode: track selections per owner { [ownerId]: processId[] }
  const [remindAllSelections, setRemindAllSelections] = useState<Record<string, string[]>>({});
  
  // Export panel state (for section export)
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false);
  const [exportingOwner, setExportingOwner] = useState<ProcessOwner | null>(null);
  const [exportingProcesses, setExportingProcesses] = useState<ProcessCertification[]>([]);
  
  // Main export panel state (triggered from parent)
  const [isMainExportOpen, setIsMainExportOpen] = useState(false);
  
  // Download certification modal state
  const [downloadModal, setDownloadModal] = useState<DownloadModalState>({
    isOpen: false,
    process: null,
    processOwnerId: '',
    selectedQuarter: null,
  });
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Initialize/update expandedOwnerIds when processOwners change
  // Expand all owners by default, including when filter is cleared
  useEffect(() => {
    if (processOwners.length > 0) {
      // Get all owner IDs that should be expanded
      const allOwnerIds = processOwners.map(po => po.id);
      // Find new owners that are not yet in expandedOwnerIds
      const newOwnerIds = allOwnerIds.filter(id => !expandedOwnerIds.includes(id));
      
      if (newOwnerIds.length > 0) {
        // Add new owners to expanded list
        setExpandedOwnerIds(prev => [...prev, ...newOwnerIds]);
      }
    }
  }, [processOwners]);
  
  // Watch for triggerExport from parent
  useEffect(() => {
    if (triggerExport) {
      setIsMainExportOpen(true);
    }
  }, [triggerExport]);
  
  // When remind-all activates, cancel per-PO mode and expand all; clear selections when deactivated
  useEffect(() => {
    if (isRemindAllMode) {
      if (activeRemindOwnerId) {
        setActiveRemindOwnerId(null);
        dispatch(exitRemindMode());
      }
      setExpandedOwnerIds(processOwners.map(po => po.id));
      // Auto-select all processes from all POs
      const allSelections: Record<string, string[]> = {};
      processOwners.forEach(owner => {
        const ownerProcesses = processesByOwnerId[owner.id] || [];
        if (ownerProcesses.length > 0) {
          allSelections[owner.id] = ownerProcesses.map(p => p.processId);
        }
      });
      setRemindAllSelections(allSelections);
    } else {
      setRemindAllSelections({});
    }
  }, [isRemindAllMode]);
  
  // Toggle owner expansion
  const handleToggleExpand = useCallback((ownerId: string) => {
    setExpandedOwnerIds(prev => 
      prev.includes(ownerId)
        ? prev.filter(id => id !== ownerId)
        : [...prev, ownerId]
    );
  }, []);
  
  // Navigate to process hierarchy
  const handleProcessClick = useCallback((processId: string) => {
    navigate(`/process-hierarchy?processId=${processId}`);
  }, [navigate]);
  
  // Navigate to certification tab with selected owner (A1.7)
  const handleOwnerNameClick = useCallback((ownerId: string) => {
    // Select the process owner in Redux
    dispatch(setSelectedProcessOwner(ownerId));
    // Navigate to certification tab
    setSearchParams({ tab: 'certification' }, { replace: true });
  }, [dispatch, setSearchParams]);
  
  // Enter remind mode for specific owner
  const handleRemindClick = useCallback((ownerId: string) => {
    setActiveRemindOwnerId(ownerId);
    dispatch(enterRemindMode());
  }, [dispatch]);
  
  // Exit remind mode
  const handleCancelRemind = useCallback(() => {
    setActiveRemindOwnerId(null);
    dispatch(exitRemindMode());
  }, [dispatch]);
  
  // Toggle process selection for remind
  const handleToggleProcessRemind = useCallback((processId: string) => {
    dispatch(toggleProcessForRemind(processId));
  }, [dispatch]);
  
  // Send reminder
  const handleSendReminder = useCallback(async () => {
    if (!activeRemindOwnerId || selectedProcessIdsForRemind.length === 0) return;
    
    setSendingReminder(true);
    try {
      await sendCertificationReminder({
        processIds: selectedProcessIdsForRemind,
        processOwnerId: activeRemindOwnerId,
      });
      message.success('Reminder sent successfully');
      handleCancelRemind();
    } catch (error) {
      console.error('Error sending reminder:', error);
      message.error('Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  }, [activeRemindOwnerId, selectedProcessIdsForRemind, handleCancelRemind]);
  
  // Toggle all processes for an owner in per-owner remind mode
  const handleToggleAllOwnerProcessesPerOwner = useCallback((ownerId: string) => {
    const ownerProcesses = processesByOwnerId[ownerId] || [];
    const allProcessIds = ownerProcesses.map(p => p.processId);
    if (allProcessIds.length === 0) return;

    const allSelected = allProcessIds.every(id => selectedProcessIdsForRemind.includes(id));
    if (allSelected) {
      dispatch(selectAllProcessesForRemind(
        selectedProcessIdsForRemind.filter(id => !allProcessIds.includes(id))
      ));
    } else {
      const merged = [...new Set([...selectedProcessIdsForRemind, ...allProcessIds])];
      dispatch(selectAllProcessesForRemind(merged));
    }
  }, [processesByOwnerId, selectedProcessIdsForRemind, dispatch]);

  // Toggle all processes for an owner in remind-all mode
  const handleRemindAllToggleAllForOwner = useCallback((ownerId: string) => {
    const ownerProcesses = processesByOwnerId[ownerId] || [];
    const allProcessIds = ownerProcesses.map(p => p.processId);
    const currentSelections = remindAllSelections[ownerId] || [];
    const allSelected = allProcessIds.length > 0 && allProcessIds.every(id => currentSelections.includes(id));

    setRemindAllSelections(prev => ({
      ...prev,
      [ownerId]: allSelected ? [] : allProcessIds,
    }));
  }, [processesByOwnerId, remindAllSelections]);

  // Remind All: toggle a process selection for a specific owner
  const handleRemindAllToggleProcess = useCallback((ownerId: string, processId: string) => {
    setRemindAllSelections(prev => {
      const ownerSelections = prev[ownerId] || [];
      const isSelected = ownerSelections.includes(processId);
      return {
        ...prev,
        [ownerId]: isSelected
          ? ownerSelections.filter(id => id !== processId)
          : [...ownerSelections, processId],
      };
    });
  }, []);
  
  // Remind All: total selected count
  const totalRemindAllSelected = useMemo(() => {
    return Object.values(remindAllSelections).reduce((sum, ids) => sum + ids.length, 0);
  }, [remindAllSelections]);
  
  // Remind All: send reminders for all selected processes (grouped by owner)
  const handleRemindAllSend = useCallback(async () => {
    const entries = Object.entries(remindAllSelections)
      .filter(([, processIds]) => processIds.length > 0);
    
    if (entries.length === 0) return;
    
    setSendingReminder(true);
    try {
      const results = await Promise.allSettled(
        entries.map(([ownerId, processIds]) =>
          sendCertificationReminder({ processIds, processOwnerId: ownerId })
        )
      );
      
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      if (failed === 0) {
        message.success(`Reminders sent to ${succeeded} process owner(s) successfully`);
      } else {
        message.warning(`${succeeded} succeeded, ${failed} failed`);
      }
      
      setRemindAllSelections({});
      onExitRemindAll?.();
    } catch (error) {
      console.error('Error sending reminders:', error);
      message.error('Failed to send reminders');
    } finally {
      setSendingReminder(false);
    }
  }, [remindAllSelections, onExitRemindAll]);
  
  // Remind All: cancel
  const handleRemindAllCancel = useCallback(() => {
    setRemindAllSelections({});
    onExitRemindAll?.();
  }, [onExitRemindAll]);
  
  // Open download certification modal
  const handleOpenDownloadModal = useCallback((process: ProcessCertification, processOwnerId: string) => {
    // Find the first available quarter (one that has certification date)
    let defaultQuarter: Quarter | null = null;
    if (process.q1SignOffDate) defaultQuarter = 1;
    else if (process.q2SignOffDate) defaultQuarter = 2;
    else if (process.q3SignOffDate) defaultQuarter = 3;
    else if (process.q4SignOffDate) defaultQuarter = 4;
    
    setDownloadModal({
      isOpen: true,
      process,
      processOwnerId,
      selectedQuarter: defaultQuarter,
    });
  }, []);
  
  // Close download modal
  const handleCloseDownloadModal = useCallback(() => {
    setDownloadModal({
      isOpen: false,
      process: null,
      processOwnerId: '',
      selectedQuarter: null,
    });
  }, []);
  
  // Handle quarter selection change
  const handleQuarterChange = useCallback((e: RadioChangeEvent) => {
    setDownloadModal(prev => ({
      ...prev,
      selectedQuarter: e.target.value as Quarter,
    }));
  }, []);
  
  // Handle download confirmation
  const handleConfirmDownload = useCallback(async () => {
    if (!downloadModal.process || !downloadModal.selectedQuarter || !downloadModal.processOwnerId) {
      return;
    }
    
    setIsDownloading(true);
    try {
      await downloadCertification(
        downloadModal.processOwnerId,
        downloadModal.process.processId,
        downloadModal.selectedQuarter
      );
      message.success(`Downloaded Q${downloadModal.selectedQuarter} certification for ${downloadModal.process.processLabel}`);
      handleCloseDownloadModal();
    } catch (error: any) {
      console.error('Download failed:', error);
      message.error(error?.message || 'Failed to download certification');
    } finally {
      setIsDownloading(false);
    }
  }, [downloadModal, handleCloseDownloadModal]);
  
  // Get available quarters with their dates for the modal
  const getQuarterOptions = useCallback(() => {
    if (!downloadModal.process) return [];
    
    const { q1SignOffDate, q2SignOffDate, q3SignOffDate, q4SignOffDate } = downloadModal.process;
    
    return [
      { quarter: 1 as Quarter, date: q1SignOffDate, label: 'Q1' },
      { quarter: 2 as Quarter, date: q2SignOffDate, label: 'Q2' },
      { quarter: 3 as Quarter, date: q3SignOffDate, label: 'Q3' },
      { quarter: 4 as Quarter, date: q4SignOffDate, label: 'Q4' },
    ];
  }, [downloadModal.process]);

  // Export handlers
  const handleOpenExportPanel = useCallback((owner: ProcessOwner, processes: ProcessCertification[]) => {
    setExportingOwner(owner);
    setExportingProcesses(processes);
    setIsExportPanelOpen(true);
  }, []);

  const handleCloseExportPanel = useCallback(() => {
    setIsExportPanelOpen(false);
    setExportingOwner(null);
    setExportingProcesses([]);
  }, []);

  // Prepare export data with formatted dates (for section export)
  const exportData = useMemo(() => {
    return exportingProcesses.map(process => ({
      processLabel: process.processLabel,
      q1SignOffDate: formatCertificationDate(process.q1SignOffDate),
      q2SignOffDate: formatCertificationDate(process.q2SignOffDate),
      q3SignOffDate: formatCertificationDate(process.q3SignOffDate),
      q4SignOffDate: formatCertificationDate(process.q4SignOffDate),
    }));
  }, [exportingProcesses]);
  
  // Main export data (for all process owners - triggered from parent)
  const mainExportData = useMemo(() => {
    const data: Record<string, unknown>[] = [];
    
    processOwners.forEach(owner => {
      const ownerProcesses = processesByOwnerId[owner.id] || [];
      ownerProcesses.forEach(process => {
        data.push({
          processOwner: owner.processOwnerName,
          process: process.processLabel,
          q1SignOff: process.q1SignOffDate ? formatCertificationDate(process.q1SignOffDate) : '-',
          q2SignOff: process.q2SignOffDate ? formatCertificationDate(process.q2SignOffDate) : '-',
          q3SignOff: process.q3SignOffDate ? formatCertificationDate(process.q3SignOffDate) : '-',
          q4SignOff: process.q4SignOffDate ? formatCertificationDate(process.q4SignOffDate) : '-',
        });
      });
    });
    
    return data;
  }, [processOwners, processesByOwnerId]);
  
  // Handle close main export panel
  const handleCloseMainExport = useCallback(() => {
    setIsMainExportOpen(false);
    onExportClose?.();
  }, [onExportClose]);
  
  if (processOwners.length === 0) {
    return (
      <EmptyStateWrapper>
        <EmptyStateText>No process owners found</EmptyStateText>
      </EmptyStateWrapper>
    );
  }
  
  return (
    <Container>
      {processOwners.map((owner, index) => (
        <ProcessOwnerSection
          key={owner.id}
          index={index}
          processOwner={owner}
          annualReportId={selectedReport?.annualReportId || ''}
          isExpanded={expandedOwnerIds.includes(owner.id)}
          isRemindMode={isRemindMode}
          activeRemindOwnerId={activeRemindOwnerId}
          selectedProcessIdsForRemind={selectedProcessIdsForRemind}
          onToggleExpand={handleToggleExpand}
          onOwnerNameClick={handleOwnerNameClick}
          onProcessClick={handleProcessClick}
          onRemindClick={handleRemindClick}
          onCancelRemind={handleCancelRemind}
          onToggleProcessRemind={handleToggleProcessRemind}
          onSendReminder={handleSendReminder}
          sendingReminder={sendingReminder}
          onDownloadCertification={handleOpenDownloadModal}
          onExportSection={handleOpenExportPanel}
          isRemindAllMode={isRemindAllMode}
          remindAllSelectedProcessIds={remindAllSelections[owner.id] || []}
          onRemindAllToggleProcess={(processId) => handleRemindAllToggleProcess(owner.id, processId)}
          onToggleAllOwnerProcesses={() => {
            if (isRemindAllMode) {
              handleRemindAllToggleAllForOwner(owner.id);
            } else {
              handleToggleAllOwnerProcessesPerOwner(owner.id);
            }
          }}
        />
      ))}
      
      {/* Remind All: global footer CTA */}
      {isRemindAllMode && (
        <RemindAllFooter>
          <StyledOutlinedButton onClick={handleRemindAllCancel}>
            Cancel
          </StyledOutlinedButton>
          <StyledFilledButton
            type="primary"
            icon={<MailOutlined />}
            loading={sendingReminder}
            disabled={totalRemindAllSelected === 0}
            onClick={handleRemindAllSend}
          >
            Send Reminder ({totalRemindAllSelected})
          </StyledFilledButton>
        </RemindAllFooter>
      )}
      
      {/* Download Certification Modal */}
      <StyledDownloadModal
        open={downloadModal.isOpen}
        onCancel={handleCloseDownloadModal}
        footer={null}
        centered
        closable={true}
        width={450}
      >
        <DownloadModalContent>
          <DownloadModalTitle>Download Certification</DownloadModalTitle>
          <DownloadModalSubtitle>
            {downloadModal.process?.processLabel}
          </DownloadModalSubtitle>
          
          <QuarterSelectionGroup>
            <Radio.Group
              value={downloadModal.selectedQuarter}
              onChange={handleQuarterChange}
              style={{ width: '100%' }}
            >
              {getQuarterOptions().map(({ quarter, date, label }) => {
                const isAvailable = !!date;
                return (
                  <QuarterOption key={quarter} $isAvailable={isAvailable}>
                    <Radio style={{gap: 4, alignItems: 'center'}} value={quarter} disabled={!isAvailable}>
                      <QuarterLabel $isAvailable={isAvailable}>{label}</QuarterLabel>
                      <QuarterDate $isAvailable={isAvailable}>
                        {isAvailable ? formatCertificationDate(date) : 'Not certified'}
                      </QuarterDate>
                    </Radio>
                  </QuarterOption>
                );
              })}
            </Radio.Group>
          </QuarterSelectionGroup>
          
          <DownloadButtonGroup>
            <DownloadCancelButton onClick={handleCloseDownloadModal} disabled={isDownloading}>
              Cancel
            </DownloadCancelButton>
            <DownloadConfirmButton
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloading}
              disabled={!downloadModal.selectedQuarter}
              onClick={handleConfirmDownload}
            >
              Download
            </DownloadConfirmButton>
          </DownloadButtonGroup>
        </DownloadModalContent>
      </StyledDownloadModal>

      {/* Export Panel (for section export) */}
      <ExportPanel
        open={isExportPanelOpen}
        onClose={handleCloseExportPanel}
        data={exportData}
        columns={[
          { key: 'processLabel', title: 'Process' },
          { key: 'q1SignOffDate', title: 'Q1' },
          { key: 'q2SignOffDate', title: 'Q2' },
          { key: 'q3SignOffDate', title: 'Q3' },
          { key: 'q4SignOffDate', title: 'Q4' },
        ]}
        fileName={`Certification_Dashboard_${exportingOwner?.processOwnerName?.replace(/\s+/g, '_') || 'Report'}`}
        title={`Certification Dashboard - ${exportingOwner?.processOwnerName || ''}`}
      />
      
      {/* Main Export Panel (triggered from parent) */}
      <ExportPanel
        open={isMainExportOpen}
        onClose={handleCloseMainExport}
        data={mainExportData}
        columns={[
          { key: 'processOwner', title: 'Process Owner', width: 25 },
          { key: 'process', title: 'Process', width: 30 },
          { key: 'q1SignOff', title: 'Q1', width: 15 },
          { key: 'q2SignOff', title: 'Q2', width: 15 },
          { key: 'q3SignOff', title: 'Q3', width: 15 },
          { key: 'q4SignOff', title: 'Q4', width: 15 },
        ]}
        fileName="Certification_Dashboard"
        title="Process Owner Certification Status"
      />
    </Container>
  );
};

export default AdminDashboardTab;
